import React, { useState, useEffect } from "react";
import products from "../../data/products.json";
import Select from "react-select";
import { motion } from "framer-motion";
import { components } from "react-select";
import "./style.css";
import { Link, useNavigate } from "react-router-dom";
import { useLanguage } from "../../context/LanguageContext";

type CompatibilityEntry = {
  brand: string;
  models: string[];
};

type Product = {
  id: string;
  name: string;
  image: string[];
  compatibility: CompatibilityEntry[];
};

const popularBrands = [
  { name: "LG", logo: "lglogo.png" },
  { name: "Samsung", logo: "samsunglogo.png" },
  { name: "Electrolux", logo: "electroluxlogo.png" },
  { name: "Panasonic", logo: "panasoniclogo.png" },
  { name: "Philips", logo: "philipslogo.png" },
];

const customSelectStyles = {
  control: (base: any, state: any) => ({
    ...base,
    minHeight: "64px",
    borderRadius: "18px",
    backgroundColor: "rgba(255,255,255,0.78)",
    borderColor: state.isFocused
      ? "rgba(11,42,87,0.30)"
      : "rgba(8,32,66,0.08)",
    boxShadow: state.isFocused
      ? "0 0 0 4px rgba(11,42,87,0.05)"
      : "0 12px 30px rgba(11,42,87,0.06)",
    "&:hover": {
      borderColor: "rgba(11,42,87,0.20)",
    },
    cursor: "pointer",
  }),

  valueContainer: (base: any) => ({
    ...base,
    paddingLeft: "18px",
  }),

  singleValue: (base: any) => ({
    ...base,
    color: "#082042",
    fontWeight: 700,
  }),

  placeholder: (base: any) => ({
    ...base,
    color: "rgba(8,32,66,0.5)",
    fontWeight: 600,
  }),

  dropdownIndicator: (base: any) => ({
    ...base,
    color: "#082042",
  }),

  indicatorSeparator: () => ({
    display: "none",
  }),

  menu: (base: any) => ({
    ...base,
    borderRadius: "18px",
    overflow: "hidden",
    border: "1px solid rgba(8,32,66,0.08)",
    boxShadow: "0 20px 40px rgba(11,42,87,0.12)",
  }),

  option: (base: any, state: any) => ({
    ...base,
    backgroundColor: state.isFocused
      ? "rgba(11,42,87,0.06)"
      : "#fff",
    color: "#082042",
    cursor: "pointer",
    fontWeight: 600,
  }),
};

export const FindProduct: React.FC = () => {
  const { t, language } = useLanguage();
  const resultsRef = React.useRef<HTMLDivElement | null>(null);
  const navigate = useNavigate();

  const [selectedBrand, setSelectedBrand] =
    useState<{ label: string; value: string } | null>(null);

  const [selectedModel, setSelectedModel] =
    useState<{ label: string; value: string } | null>(null);

  const [modelOptions, setModelOptions] = useState<
    { label: string; value: string }[]
  >([]);

  const [filteredProducts, setFilteredProducts] = useState<Product[]>([]);

  const brandOptions = Array.from(
    new Set(products.flatMap((p) => p.compatibility.map((c) => c.brand)))
  )
    .map((brand) => ({
      label: brand,
      value: brand,
    }))
    .sort((a, b) => a.label.localeCompare(b.label));

  useEffect(() => {
    if (!selectedBrand) {
      setModelOptions([]);
      setSelectedModel(null);
      return;
    }

    const models = new Set<string>();

    products.forEach((product) => {
      product.compatibility.forEach((c) => {
        if (c.brand === selectedBrand.value) {
          c.models.forEach((m) => models.add(m));
        }
      });
    });

    setModelOptions(
      Array.from(models)
        .map((m) => ({
          label: m,
          value: m,
        }))
        .sort((a, b) => a.label.localeCompare(b.label))
    );

    setSelectedModel(null);
  }, [selectedBrand]);

  useEffect(() => {
    if (!selectedBrand || !selectedModel) {
      setFilteredProducts([]);
      return;
    }

    const matches = products.filter((product) =>
      product.compatibility.some(
        (c) =>
          c.brand === selectedBrand.value &&
          c.models.includes(selectedModel.value)
      )
    );

    setFilteredProducts(matches);
    setTimeout(() => {
      resultsRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }, 120);
  }, [selectedBrand, selectedModel]);

  const resetFilters = () => {
    setSelectedBrand(null);
    setSelectedModel(null);
    setFilteredProducts([]);
  };

  const AnimatedMenu = (props: any) => (
    <motion.div
      initial={{ opacity: 0, scale: 0.98 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.15 }}
    >
      <components.Menu {...props} />
    </motion.div>
  );

  return (
    <div className="main-filter-div">
      <div className="ambient-blur ambient-1"></div>
      <div className="ambient-blur ambient-2"></div>
      <div className="ambient-blur ambient-3"></div>

      <div className="showroom-glow showroom-glow-1"></div>
      <div className="showroom-glow showroom-glow-2"></div>

      <div className="background-light-beam"></div>

      <div className="floating-circle floating-circle-1"></div>
      <div className="floating-circle floating-circle-2"></div>

      <div className="floating-outline floating-outline-1"></div>
      <div className="floating-outline floating-outline-2"></div>

      <div className="background-grid-dots"></div>

      <div className="luxury-shadow-overlay"></div>

      <div className="page-bg-shape-left"></div>

      <div className="filter-page">
        <button
          className="back-button-premium"
          onClick={() => navigate(-1)}
        >
          <img src="arrow1.png" alt="text" className="backhomebutton" />
        </button>

        <div className="filter-hero">
          <img src="filter.png" alt="" />
          <h1>{t("find_compatible_products")}</h1>
          <p>{t("select_brand_model_help")}</p>
        </div>

        <div className="quick-filters-wrapper">
          <span className="popular-brands-title">
            {t("popular_brands_label")}
          </span>

          <div className="brands-grid1">
            {popularBrands.map((brand) => (
              <motion.button
                key={brand.name}
                className={`brand-btn ${
                  selectedBrand?.value === brand.name ? "active" : ""
                }`}
                onClick={() =>
                  setSelectedBrand({
                    label: brand.name,
                    value: brand.name,
                  })
                }
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.96 }}
              >
                <img src={brand.logo} alt={brand.name} />
              </motion.button>
            ))}
          </div>
        </div>

        <motion.div
          className="filters-container"
          initial={{ opacity: 0, y: 25 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <div className="filter-input-wrapper">
            <label>{t("brand")}</label>
            <Select
              options={brandOptions}
              value={selectedBrand}
              onChange={(option) => setSelectedBrand(option)}
              placeholder={t("select_brand")}
              isClearable
              styles={customSelectStyles}
              components={{ Menu: AnimatedMenu }}
            />
          </div>

          <div className="filter-input-wrapper">
            <label>{t("model")}</label>
            <Select
              options={modelOptions}
              value={selectedModel}
              onChange={(option) => setSelectedModel(option)}
              placeholder={t("select_model")}
              isClearable
              isDisabled={!modelOptions.length}
              styles={customSelectStyles}
              components={{ Menu: AnimatedMenu }}
            />
          </div>
        </motion.div>

        {selectedBrand &&
          selectedModel &&
          filteredProducts.length > 0 && (
            <div className="results-top-line">
              {t("showing_results_for")
                .replace(
                  "{{count}}",
                  String(filteredProducts.length)
                )
                .replace(
                  "{{plural}}",
                  language === "en" && filteredProducts.length > 1 ? "s" : ""
                )
                .replace("{{brand}}", selectedBrand.label)
                .replace("{{model}}", selectedModel.label)}
            </div>
          )}

        <div className="results-wrapper" ref={resultsRef}>
          {filteredProducts.map((product, index) => (
            <motion.div
              key={product.id}
              className="premium-product-card"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{
                duration: 0.45,
                delay: index * 0.05,
              }}
            >
              <div className="product-left-side">
                <div className="product-image-platform"></div>
                <img
                  src={product.image[0]}
                  alt={product.name}
                  className="premium-product-image"
                />
              </div>

              <div className="product-middle-side">
                <div className="product-middle-content">
                  <h2>{product.name}</h2>
                  <div className="product-features-list">
                    <div className="feature-row">
                      <img src="layers.png" alt="" />
                      <div>
                        <strong>4 շերտանոց պաշտպանություն</strong>
                        <span>Ամուր և հուսալի կառուցվածք</span>
                      </div>
                    </div>

                    <div className="feature-row">
                      <img src="info.png" alt="" />
                      <div>
                        <strong>Համատեղելի մոդելի հետ</strong>
                        <span>
                          {selectedBrand?.label} — {selectedModel?.label}
                        </span>
                      </div>
                    </div>

                    <div className="feature-row">
                      <img src="vacuum_cleaner_icon.png" alt="" />
                      <div>
                        <strong>Մաքուր օդ և ուժեղ ֆիլտրացիա</strong>
                        <span>Նախատեսված է ամենօրյա օգտագործման համար</span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="action-buttons-wrapper">
                  <Link
                    to={`/selling/${product.id}`}
                    className="premium-link-button"
                  >
                    {t("view_details")}
                  </Link>

                  <button
                    onClick={resetFilters}
                    className="premium-link-button secondary-action-btn"
                  >
                    {t("reset_filters")}
                  </button>
                </div>

              </div>

              <div className="product-right-side">
                <div className="vacuum-platform"></div>
                <img
                  src={product.image[1]}
                  alt=""
                  className="vacuum-image-temp"
                />
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  );
};
